import { z } from "zod";
import { entityHydrationRegistry } from "@/lib/kernel/entity-hydration/registry";
import type {
  Contribution,
  EntitySourceProducer,
} from "@/lib/kernel/entity-hydration/types";
import type { ProfileId } from "@/lib/kernel/relations/types";
import { readPreparedSpellIds as readPreparedSpellIdsForCycle } from "@/lib/rules/dnd5e/spellcasting/prepared-spells";
import type { Dnd5eSpellSchool } from "@/lib/rules/dnd5e/vocabulary/spell-school";

export const WIZARD_SPELLBOOK_ITEM_DEF_ID = "item_wizard_spellbook";
export const SPELLBOOK_ENTRIES_COMPONENT_ID = "spellbook_entries";
export const WIZARD_SPELLCASTING_PROFILE_ID =
  "profile_wizard_spellcasting" as ProfileId;
export const WIZARD_EVOCATION_PROFILE_ID = "profile_wizard_school_of_evocation";
export const WIZARD_MEMORIZE_SPELL_MIN_LEVEL = 5;
export const WIZARD_TRANSCRIPTION_HOURS_PER_SPELL_LEVEL = 2;
export const WIZARD_TRANSCRIPTION_GP_PER_SPELL_LEVEL = 50;

export const WIZARD_STARTING_SPELLBOOK_SPELL_IDS = [
  "action_detect_magic",
  "action_thunderwave",
  "action_lightning_bolt",
] as const;

const SpellbookEntriesSchema = z.object({
  spellIds: z.array(z.string()).default([]),
});
const ComponentBagWithSpellbookEntriesSchema = z
  .object({
    [SPELLBOOK_ENTRIES_COMPONENT_ID]: SpellbookEntriesSchema.optional(),
  })
  .passthrough();
const ComponentBagWithWizardSpellcastingSchema = z
  .object({
    profile_wizard_spellcasting: z.unknown().optional(),
  })
  .passthrough();

export type SpellbookEntries = z.infer<typeof SpellbookEntriesSchema>;
export type WizardSpellMetadata = {
  readonly spellLevel: number;
  readonly spellSchool?: Dnd5eSpellSchool;
};

type ComponentCarrier = {
  readonly state?: {
    readonly components?: unknown;
    readonly resources?: unknown;
  };
};

type SpellbookCandidate = ComponentCarrier & {
  readonly id?: string;
  readonly definitionId?: string;
  readonly containedIn?: string | null;
};

export const readWizardSpellbookSpellIds = (
  entity: ComponentCarrier
): string[] => {
  const parsed = ComponentBagWithSpellbookEntriesSchema.safeParse(
    entity.state?.components ?? {}
  );
  const entries = parsed.success
    ? parsed.data[SPELLBOOK_ENTRIES_COMPONENT_ID]
    : undefined;
  if (!entries) {
    return [];
  }
  return Array.from(new Set(entries.spellIds));
};

export const hasWizardSpellcastingProfile = (
  entity: ComponentCarrier
): boolean =>
  ComponentBagWithWizardSpellcastingSchema.safeParse(
    entity.state?.components ?? {}
  ).data?.profile_wizard_spellcasting !== undefined;

export const wizardClassLevel = (entity: ComponentCarrier): number => {
  const rawProfile = (
    entity.state?.components as
      | Record<string, { readonly level?: unknown } | undefined>
      | undefined
  )?.profile_wizard;
  return typeof rawProfile?.level === "number" &&
    Number.isFinite(rawProfile.level)
    ? rawProfile.level
    : 0;
};

export const hasWizardEvocationSavant = (entity: ComponentCarrier): boolean =>
  (entity.state?.components as Record<string, unknown> | undefined)?.[
    WIZARD_EVOCATION_PROFILE_ID
  ] !== undefined;

export const readPreparedSpellIds = (entity: ComponentCarrier): string[] => {
  const state = entity.state;
  if (!state) {
    return [];
  }
  return readPreparedSpellIdsForCycle({ state });
};

export const readGoldCurrent = (entity: ComponentCarrier): number | null => {
  const current = (
    entity.state?.resources as
      | Record<string, { readonly current?: unknown } | undefined>
      | undefined
  )?.gold?.current;
  return typeof current === "number" && Number.isFinite(current)
    ? current
    : null;
};

export const wizardTranscriptionCost = (
  metadata: WizardSpellMetadata,
  actor: ComponentCarrier
): {
  readonly hours: number;
  readonly gp: number;
  readonly discounted: boolean;
} => {
  const baseGp = metadata.spellLevel * WIZARD_TRANSCRIPTION_GP_PER_SPELL_LEVEL;
  const discounted =
    metadata.spellSchool === "evocation" && hasWizardEvocationSavant(actor);
  return {
    hours: metadata.spellLevel * WIZARD_TRANSCRIPTION_HOURS_PER_SPELL_LEVEL,
    gp: discounted ? Math.floor(baseGp / 2) : baseGp,
    discounted,
  };
};

export const findDirectWizardSpellbooks = (
  actorId: string,
  entities: Iterable<SpellbookCandidate>
): SpellbookCandidate[] =>
  Array.from(entities).filter(
    (entity) =>
      entity.definitionId === WIZARD_SPELLBOOK_ITEM_DEF_ID &&
      entity.containedIn === actorId
  );

const wizardSpellbookProducer: EntitySourceProducer<"dnd5e"> = (input) => {
  if (!hasWizardSpellcastingProfile(input.entity)) {
    return [];
  }

  const spellIds = new Set(
    findDirectWizardSpellbooks(
      input.entity.id,
      Object.values(input.state.entities)
    ).flatMap((book) => readWizardSpellbookSpellIds(book))
  );

  return Array.from(spellIds).map(
    (actionId): Contribution<"dnd5e"> => ({
      kind: "action-grant",
      actionId,
      requiresPrepared: true,
      grantsRitualCasting: true,
      source: {
        contributor: "profile",
        profileId: WIZARD_SPELLCASTING_PROFILE_ID,
      },
    })
  );
};

export const registerWizardSpellbookProducer = (): void => {
  entityHydrationRegistry.registerEntitySourceProducer<"dnd5e">(
    wizardSpellbookProducer
  );
};
